import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Tag, Copy, Sparkle } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

const SHOW_DELAY_MS = 1400;

// Shown once per page load: every reload shows the offer again, but closing
// it keeps it closed while moving between pages (module state resets only on
// a full reload).
let dismissedThisLoad = false;

export function formatDiscount(coupon) {
    return coupon.type === "percent"
        ? `${Number(coupon.value)}% OFF`
        : `₹${Number(coupon.value)} OFF`;
}

export function formatValidUntil(value) {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}

export default function OfferPopup() {
    const { user } = useAuth();
    const loc = useLocation();
    const nav = useNavigate();
    const [coupon, setCoupon] = useState(null);
    const [visible, setVisible] = useState(false);

    // Marketing pop-up: only for visitors browsing the public site, not
    // inside the studio/admin — and admins manage offers, they aren't the
    // audience for them.
    const eligible = useMemo(
        () =>
            !loc.pathname.startsWith("/studio") &&
            !loc.pathname.startsWith("/admin") &&
            loc.pathname !== "/offer" &&
            user?.role !== "admin",
        [loc.pathname, user],
    );

    useEffect(() => {
        if (!eligible || dismissedThisLoad) {
            setVisible(false);
            return;
        }

        let cancelled = false;
        let timer;
        api.get("/coupons/active")
            .then(({ data }) => {
                if (cancelled || !data.coupon) return;
                setCoupon(data.coupon);
                timer = setTimeout(() => {
                    if (!dismissedThisLoad) setVisible(true);
                }, SHOW_DELAY_MS);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eligible]);

    const dismiss = () => {
        setVisible(false);
        dismissedThisLoad = true;
    };

    const copyCode = () => {
        navigator.clipboard?.writeText(coupon.code);
        toast.success(`Code "${coupon.code}" copied!`);
    };

    const claim = () => {
        // Pricing.jsx picks this up and pre-fills + auto-applies the code.
        sessionStorage.setItem("pending_coupon_code", coupon.code);
        dismiss();
        nav("/pricing");
    };

    if (!visible || !coupon) return null;
    const validUntil = formatValidUntil(coupon.valid_until);

    return (
        <div
            className="fixed inset-0 z-[60] bg-zinc-950/60 flex items-center justify-center p-4 backdrop-blur-sm"
            data-testid="offer-popup"
            onClick={dismiss}
        >
            <div
                className="relative w-full max-w-sm bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={dismiss}
                    className="absolute top-3 right-3 z-10 text-white/80 hover:text-white"
                    aria-label="Close"
                    data-testid="offer-popup-close"
                >
                    <X size={20} weight="bold" />
                </button>

                <div className="relative g-sunset text-white px-6 pt-8 pb-10 text-center overflow-hidden">
                    <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                    <div className="absolute -bottom-10 -left-6 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                    <Sparkle
                        size={22}
                        weight="fill"
                        className="mx-auto mb-2 text-amber-200 animate-[pulse_2s_ease-in-out_infinite]"
                    />
                    <div className="text-xs font-mono uppercase tracking-widest text-white/80">
                        Limited-time offer
                    </div>
                    <div className="font-display font-black text-5xl tracking-tighter mt-1">
                        {formatDiscount(coupon)}
                    </div>
                    <p className="text-sm text-white/90 mt-2">
                        On any credit package — apply the code at checkout.
                    </p>
                </div>

                <div className="p-6 space-y-4">
                    <button
                        onClick={copyCode}
                        className="w-full flex items-center justify-between gap-3 border-2 border-dashed border-zinc-300 hover:border-zinc-400 px-4 py-3 transition-colors"
                        data-testid="offer-popup-copy"
                    >
                        <span className="flex items-center gap-2 font-mono font-bold text-lg tracking-widest text-zinc-900">
                            <Tag size={18} weight="fill" className="text-pink-600" />
                            {coupon.code}
                        </span>
                        <span className="flex items-center gap-1 text-xs font-semibold text-zinc-500 uppercase tracking-wide">
                            <Copy size={14} />
                            Copy
                        </span>
                    </button>

                    {validUntil && (
                        <p className="text-center text-xs text-zinc-500">
                            Offer valid until {validUntil}
                        </p>
                    )}

                    <button
                        onClick={claim}
                        className="btn-primary w-full"
                        data-testid="offer-popup-claim"
                    >
                        Claim this offer
                    </button>
                    <button
                        onClick={() => {
                            dismiss();
                            nav("/offer");
                        }}
                        className="w-full text-center text-sm font-semibold text-pink-600 hover:underline"
                        data-testid="offer-popup-details"
                    >
                        See offer details & prices
                    </button>
                    <button
                        onClick={dismiss}
                        className="w-full text-center text-xs text-zinc-400 hover:text-zinc-600"
                    >
                        Maybe later
                    </button>
                </div>
            </div>
        </div>
    );
}
